import Link from 'next/link'

import { Poster } from '@/app/components/atoms/Poster'
import { Price } from '@/app/components/atoms/Price'
import { PriceTag } from '@/app/components/atoms/PriceTag'
import { Sticker } from '@/app/components/atoms/Sticker'
import { Tag } from '@/app/components/atoms/Tag'
import { cx } from '@/app/lib/utils'

import styles from './RecentlyViewedCard.module.scss'
import type { RecentlyViewedCardProps } from './types'

/**
 * Tarjeta "solo portada" del carril de vistos recientemente: imagen 4:5 con
 * título, fecha y precio sobreimpresos, visibles al pasar el cursor o al recibir
 * el foco (paridad con el patrón del proyecto de referencia de diseño).
 *
 * Presentacional pura: sin estado, efectos ni lógica de negocio — el estado de
 * compra, el precio y la fecha llegan ya resueltos por `mapPerformanceToCard`.
 * Por eso **no lleva test propio** (Constitución, Principio IX). Renderizado
 * defensivo: sin imagen pinta solo el fondo placeholder, y fecha/precio/etiqueta
 * se omiten si no hay dato.
 *
 * Vive colocada dentro de la sección porque hoy es su único consumidor; si
 * aparece un segundo, se promueve a `components/shared/cards/`.
 */
export function RecentlyViewedCard({
  title,
  href,
  img,
  date,
  priceDisplay,
  tag,
  sticker,
  imageBadge,
}: RecentlyViewedCardProps) {
  const hasPriceBadge = Boolean(priceDisplay?.tag || priceDisplay?.hasAppPromotions)

  return (
    <article className={styles.card}>
      <Link href={href} className={styles.link}>
        <div className={cx(styles.imageWrap, imageBadge && styles.imageWrapMuted)}>
          {img && <Poster src={img} alt="" className={styles.poster} />}

          {/* Categoría — se oculta con estado de compra: la imagen ya comunica
              el estado y evita ruido visual (igual que `VerticalCard`). */}
          {/* Categoría + sticker apilados en la esquina superior izquierda (card
              pequeña: el sticker va bajo la categoría, no abajo, donde iría la
              información al hover). Ambos se ocultan con estado de compra. */}
          {(tag || sticker) && !imageBadge && (
            <div className={cx(styles.topLeftStack, hasPriceBadge && styles.topLeftStackWithBadge)}>
              {tag && <Tag variant="black">{tag}</Tag>}
              {sticker && <Sticker>{sticker}</Sticker>}
            </div>
          )}

          <PriceTag
            tag={priceDisplay?.tag ?? null}
            hasAppPromotions={priceDisplay?.hasAppPromotions}
            className={styles.tagTopRight}
          />

          {imageBadge && (
            <div className={styles.imageBadge}>
              <Tag variant="status">{imageBadge}</Tag>
            </div>
          )}
        </div>

        {/* Scrim + información, ocultos hasta hover/focus (gobernado por CSS).
            El scrim es decorativo; el texto NO se oculta a lectores de pantalla:
            debe seguir anunciando de qué evento es la tarjeta. */}
        <div className={styles.scrim} aria-hidden="true" />
        <div className={styles.info}>
          <h3 className={styles.title}>{title}</h3>
          {date && <p className={styles.meta}>{date}</p>}
          {priceDisplay && (
            <Price
              amount={priceDisplay.amount}
              isFree={priceDisplay.isFree}
              originalAmount={priceDisplay.originalAmount ?? undefined}
              variant={priceDisplay.originalAmount != null ? 'offer' : 'default'}
              showFrom
              className={styles.price}
            />
          )}
        </div>
      </Link>
    </article>
  )
}
